import { useEffect, useState } from 'react';

import {
  StyledContent,
  StyledContentWrapper,
  StyledLabel,
  StyledLabelsWrapper,
  StyledWrapper,
} from './Tabs.style';
import type { TabStyle } from './Tabs.style';

interface Tab {
  label: string;
  content: React.ReactNode | string;
  onClick?: (index: number) => void;
}

export interface TabsProps {
  activeIndex?: number;
  items: Array<Tab>;
  variant?: TabStyle;
  onTabChange?: (index: number) => void;
}

export function TabsLabels({
  activeIndex,
  items,
  onClick,
  variant = 'default',
}: {
  activeIndex?: number;
  items: Array<Tab>;
  onClick: Tab['onClick'];
  variant?: TabsProps['variant'];
}) {
  return (
    <StyledLabelsWrapper variant={variant}>
      {items.map((item, index) => (
        <StyledLabel
          variant={variant}
          key={`tab-label-${item.label}`}
          data-tab-label={item.label}
          isActive={activeIndex === index}
          onClick={() => onClick?.(index)}
        >
          {item.label}
        </StyledLabel>
      ))}
    </StyledLabelsWrapper>
  );
}

export function TabsContent({
  activeIndex,
  items,
  loadedTab,
}: {
  activeIndex: number;
  items: Array<Tab>;
  loadedTab: Record<number, boolean>;
}) {
  return (
    <StyledContentWrapper className="tabs-content-wrapper">
      {items.map(
        (item, index) =>
          loadedTab[index] && (
            <StyledContent
              key={`tab-label-${item.label}`}
              data-tab-content={item.label}
              isActive={activeIndex === index}
              className="tabs-content"
            >
              {item.content}
            </StyledContent>
          )
      )}
    </StyledContentWrapper>
  );
}

export function Tabs({ activeIndex, items, onTabChange, variant = 'default' }: TabsProps) {
  const [activeTab, setActiveTab] = useState(activeIndex ?? 0);
  const [loadedTab, setLoadedTab] = useState<Record<number, boolean>>({
    [activeTab]: true,
  });

  useEffect(() => {
    const newActiveTab = activeIndex ?? 0;

    if (newActiveTab === activeTab) return;

    setActiveTab(newActiveTab);
    setLoadedTab((prev) => ({ ...prev, [newActiveTab]: true }));
    onTabChange?.(newActiveTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const handleTabClick: Tab['onClick'] = (key) => {
    setActiveTab(key);
    setLoadedTab((prev) => ({ ...prev, [key]: true }));
    onTabChange?.(key);
  };

  return (
    <StyledWrapper className="tabs-wrapper">
      <TabsLabels
        items={items}
        activeIndex={activeTab}
        onClick={handleTabClick}
        variant={variant}
      />

      <TabsContent items={items} activeIndex={activeTab} loadedTab={loadedTab} />
    </StyledWrapper>
  );
}
